
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Facebook, Share2, ExternalLink, AlertTriangle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface FacebookShareDialogProps {
  postContent?: string;
  postUrl?: string;
  imageUrl?: string;
  title?: string;
  description?: string;
}

export const FacebookShareDialog = ({ 
  postContent, 
  postUrl, 
  imageUrl, 
  title, 
  description 
}: FacebookShareDialogProps) => {
  const [sharing, setSharing] = useState(false);
  const { toast } = useToast();

  const shareToFacebook = () => {
    setSharing(true);
    
    try {
      const urlToShare = postUrl || (typeof window !== 'undefined' ? window.location.href : '');
      
      const shareParams = new URLSearchParams({
        u: urlToShare,
        ...(title && { quote: title }),
        ...(description && { quote: description || postContent || '' }),
      });

      const shareUrl = `https://www.facebook.com/sharer/sharer.php?${shareParams.toString()}`;
      
      // Only try to open popup if we're in a browser environment
      if (typeof window !== 'undefined') {
        const popup = window.open(
          shareUrl,
          'facebook-share-dialog',
          'width=626,height=436,toolbar=0,menubar=0,location=0,status=0,scrollbars=1,resizable=1'
        );

        if (popup) {
          toast({
            title: "Opening Facebook Share",
            description: "Facebook share dialog is opening in a new window.",
          });

          const checkClosed = setInterval(() => {
            if (popup.closed) {
              clearInterval(checkClosed);
              setSharing(false);
              toast({
                title: "Share Window Closed",
                description: "You can try sharing again if needed.",
              });
            }
          }, 1000);
          
          setTimeout(() => {
            clearInterval(checkClosed);
            if (!popup.closed) {
              popup.close();
            }
            setSharing(false);
          }, 300000);
        } else {
          throw new Error('Popup blocked or failed to open');
        }
      }
    } catch (error) {
      console.error('Facebook share error:', error);
      setSharing(false);
      
      toast({
        title: "Share Dialog Blocked",
        description: "Please allow popups or use the direct link option.",
        variant: "destructive",
      });
    }
  };

  const directShare = () => {
    const urlToShare = postUrl || (typeof window !== 'undefined' ? window.location.href : '');
    const shareParams = new URLSearchParams({
      u: urlToShare,
      ...(title && { quote: title }),
    });
    const shareUrl = `https://www.facebook.com/sharer/sharer.php?${shareParams.toString()}`;
    
    if (typeof window !== 'undefined') {
      window.open(shareUrl, '_blank');
      
      toast({
        title: "Facebook Share Opened",
        description: "Facebook share page opened in a new tab.",
      });
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
        <Facebook className="h-5 w-5 text-blue-600" />
        <div className="flex-1">
          <h4 className="font-medium text-blue-800">Share to Facebook</h4>
          <p className="text-sm text-blue-600">
            Share this content using Facebook's native sharing interface
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2">
        <Button
          onClick={shareToFacebook}
          disabled={sharing}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white"
        >
          <div className="flex items-center gap-2">
            <Share2 size={16} />
            <span>{sharing ? 'Opening Share Dialog...' : 'Share in Popup'}</span>
            <ExternalLink size={14} />
          </div>
        </Button>
        
        <Button
          onClick={directShare}
          variant="outline"
          className="w-full border-blue-200 text-blue-700 hover:bg-blue-50"
        >
          <div className="flex items-center gap-2">
            <Facebook size={16} />
            <span>Open in New Tab</span>
            <ExternalLink size={14} />
          </div>
        </Button>
      </div>

      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs text-amber-800 mb-2 font-medium">
              Preview Environment Notice:
            </p>
            <ul className="text-xs text-amber-700 space-y-1">
              <li>• Facebook sharing may appear blocked in this preview</li>
              <li>• The sharing will work properly on the deployed site</li>
              <li>• Use "Open in New Tab" as an alternative</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
